/**
 * Calendar Page
 * 
 * Displays events in a calendar view with:
 * - Calendar library integration (react-big-calendar)
 * - Color coding by event type
 * - Canceled events with strikethrough
 * - Click event to view details
 * 
 * Requirements: 2.1, 2.2, 2.3, 2.4
 */

import { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar as BigCalendar, dateFnsLocalizer, Event as CalendarEvent, View } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import useEventsStore from '../store/eventsStore';
import { useAuth } from '../auth/AuthProvider';
import { Event, restClient, EventType } from '../api/rest-client';
import { Card } from '../components/common/Card';
import { LoadingSpinner } from '../components/common/LoadingSpinner';
import 'react-big-calendar/lib/css/react-big-calendar.css';

// Configure date-fns localizer for react-big-calendar
const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

/**
 * Convert API Event to Calendar Event
 */
function eventToCalendarEvent(event: Event): CalendarEvent {
  return {
    title: event.event_name,
    start: new Date(event.event_starttime),
    end: new Date(event.event_endtime),
    resource: event, // Store full event data
  };
}

/**
 * Calendar Page Component
 */
export function Calendar() {
  const navigate = useNavigate();
  
  // Get events from store
  const {
    events,
    loading,
    error,
    fetchEvents,
  } = useEventsStore();

  // Local state for view
  const [view, setView] = useState<View>('month');
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);

  // Get auth state for role-based filtering
  const { isAdmin, isCoach, user } = useAuth();

  // Compute user's associated rider IDs for non-admin/non-coach filtering
  const userRiderIds = useMemo(() => {
    if (isAdmin || isCoach) return undefined;
    if (!user) return undefined;
    const associations = user.rider_associations;
    if (!associations || associations.length === 0) return undefined;
    return associations.map(a => a.rider_id);
  }, [isAdmin, isCoach, user]);

  // Fetch events and event types on mount
  useEffect(() => {
    fetchEvents(null as any, userRiderIds); // Pass null to fetch all events
    fetchEventTypes();
  }, [fetchEvents, userRiderIds]);

  /**
   * Fetch event types for color mapping
   */
  const fetchEventTypes = useCallback(async () => {
    try {
      const types = await restClient.getEventTypes();
      setEventTypes(types);
    } catch (error) {
      // Silent error handling - will use default colors
    }
  }, []);

  /**
   * Get color for event type badge
   */
  const getEventTypeColor = useCallback((eventTypeName: string): string => {
    const eventType = eventTypes.find(et => et.name === eventTypeName);
    return eventType?.color || '#435d7d'; // Default to primary blue
  }, [eventTypes]);

  // Convert to calendar events
  const calendarEvents = useMemo(() => {
    return events.map(eventToCalendarEvent);
  }, [events]);

  /**
   * Handle event click - navigate to event detail
   * Requirements: 2.4
   */
  const handleSelectEvent = useCallback((calendarEvent: CalendarEvent) => {
    const event = calendarEvent.resource as Event;
    navigate(`/events/${event.event_id}`);
  }, [navigate]);

  /**
   * Custom event style getter - color code by type and show canceled
   * Requirements: 2.1, 2.3
   */
  const eventStyleGetter = useCallback((calendarEvent: CalendarEvent) => {
    const event = calendarEvent.resource as Event;
    const color = getEventTypeColor(event.event_type);
    
    const style: React.CSSProperties = {
      backgroundColor: color,
      borderRadius: '4px',
      opacity: event.event_status === 'canceled' ? 0.6 : 1, // null/undefined treated as active
      color: 'white',
      border: '0px',
      display: 'block',
      textDecoration: event.event_status === 'canceled' ? 'line-through' : 'none',
    };

    return { style };
  }, [getEventTypeColor]);

  /**
   * Custom event component - show canceled with strikethrough
   */
  const EventComponent = ({ event }: { event: CalendarEvent }) => {
    const apiEvent = event.resource as Event;
    return (
      <span
        style={{
          textDecoration: apiEvent.event_status === 'canceled' ? 'line-through' : 'none', // null/undefined treated as active
        }}
      >
        {event.title}
      </span>
    );
  };

  if (loading && events.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Event Calendar</h1>
      </div>

      {loading && (
        <div className="mb-6">
          <LoadingSpinner size="sm" />
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Calendar */}
      <Card>
        <div style={{ height: '700px' }}>
          <BigCalendar
            localizer={localizer}
            events={calendarEvents}
            startAccessor="start"
            endAccessor="end"
            view={view}
            onView={setView}
            onSelectEvent={handleSelectEvent}
            eventPropGetter={eventStyleGetter}
            components={{
              event: EventComponent,
            }}
            popup
            selectable
            style={{ height: '100%' }}
          />
        </div>
      </Card>

      {/* Calendar Subscription */}
      <div className="mt-6">
        <Card>
          <h3 className="text-lg font-semibold mb-4">📅 Subscribe to Calendar</h3>
          <p className="text-gray-700 mb-4">
            Add Blue Clipboard events to your personal calendar app (iPhone, Google Calendar, Outlook, etc.)
          </p>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <h4 className="font-semibold text-blue-900 mb-2">Quick Subscribe (iOS/macOS)</h4>
            <p className="text-sm text-blue-800 mb-3">
              Click the button below to automatically add the calendar to your device:
            </p>
            <a
              href="webcal://xs3sofzrxh.execute-api.us-east-2.amazonaws.com/v1/events/ical"
              className="inline-block px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors"
            >
              Subscribe to Calendar
            </a>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 mb-2">Manual Setup (All Devices)</h4>
            <p className="text-sm text-gray-700 mb-2">
              Copy this URL and add it as a subscribed calendar in your calendar app:
            </p>
            <div className="bg-white border border-gray-300 rounded p-2 mb-3">
              <code className="text-sm text-gray-800 break-all">
                https://xs3sofzrxh.execute-api.us-east-2.amazonaws.com/v1/events/ical
              </code>
            </div>
            
            <details className="text-sm text-gray-600">
              <summary className="cursor-pointer font-medium text-gray-700 hover:text-gray-900">
                Show setup instructions
              </summary>
              <div className="mt-3 space-y-3">
                <div>
                  <p className="font-medium text-gray-800">iPhone/iPad:</p>
                  <ol className="list-decimal list-inside ml-2 space-y-1">
                    <li>Open Settings → Calendar → Accounts</li>
                    <li>Tap "Add Account" → "Other"</li>
                    <li>Tap "Add Subscribed Calendar"</li>
                    <li>Paste the URL above and tap "Next"</li>
                    <li>Name it "Blue Clipboard Events" and tap "Save"</li>
                  </ol>
                </div>
                
                <div>
                  <p className="font-medium text-gray-800">Google Calendar:</p>
                  <ol className="list-decimal list-inside ml-2 space-y-1">
                    <li>Open Google Calendar on desktop</li>
                    <li>Click the "+" next to "Other calendars"</li>
                    <li>Select "From URL"</li>
                    <li>Paste the URL above and click "Add calendar"</li>
                  </ol>
                </div>
                
                <div>
                  <p className="font-medium text-gray-800">Outlook:</p>
                  <ol className="list-decimal list-inside ml-2 space-y-1">
                    <li>Open Outlook Calendar</li>
                    <li>Click "Add calendar" → "Subscribe from web"</li>
                    <li>Paste the URL above</li>
                    <li>Name it "Blue Clipboard Events" and click "Import"</li>
                  </ol>
                </div>
              </div>
            </details>
          </div>

          <p className="text-xs text-gray-500 mt-4">
            ℹ️ Your calendar will automatically update when new events are added or changed.
          </p>
        </Card>
      </div>

      {/* Legend */}
      <div className="mt-6">
        <Card>
          <h3 className="text-lg font-semibold mb-4">Event Types</h3>
          <div className="flex flex-wrap gap-4">
            {eventTypes.map((eventType) => (
              <div key={eventType.type_id} className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: eventType.color || '#435d7d' }}
                />
                <span className="text-sm text-gray-700">{eventType.name}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 text-sm text-gray-600">
            <p>• Canceled events appear faded with strikethrough</p>
            <p>• Click any event to view details</p>
          </div>
        </Card>
      </div>
    </div>
  );
}

export default Calendar;
